/**
 * useHotkeyEngine - Centralized keyboard shortcut engine
 *
 * Replaces per-station useEffect keyboard handlers with a single hook
 * that matches configured bindings against keyboard events.
 *
 * Supports two binding types:
 * - Combo: modifier(s) + key pressed simultaneously (fires on keydown)
 * - Hold: key held for N milliseconds (fires on keyup after threshold)
 *
 * Safety features:
 * - Focus guard: skips events on inputs/textareas/scanner fields
 * - Pause prop: disables processing during modals/dialogs
 * - Flush on pause transition: clears dangling hold state
 * - Window blur: clears all hold state
 * - comboFiredSet: prevents hold firing after combo on same key
 * - keyup always processes (even when paused) for hold cleanup
 * - No stopPropagation: only preventDefault (preserves other handlers)
 *
 * IMPORTANT: All call sites must wrap `handlers` in useMemo to prevent
 * excessive listener teardown/setup on every render.
 */

import { useEffect, useRef, useState } from 'react';
import type { HotkeyAction, HotkeyBinding, HotkeyMap } from './hotkey-types';
import { DEFAULT_HOTKEY_MAP } from './hotkey-types';

interface UseHotkeyEngineOpts {
  /** When true, combo detection is disabled. keyup still processes for cleanup. */
  paused?: boolean;
}

interface UseHotkeyEngineResult {
  /** The key currently being held (for UI feedback), or null */
  activeHold: string | null;
}

/**
 * Check if the currently focused element should prevent hotkey processing.
 * Returns true if focus is on a regular input, textarea, select, or contentEditable.
 *
 * NOTE: [data-scanner-input] fields are explicitly EXCLUDED from this check.
 * Scanner inputs are passive capture fields, not user-typed forms. The engine's
 * binding safety (combos require modifiers, holds require 300ms+) already prevents
 * scanner interference. Blocking on scanner focus would break hold bindings after
 * every scan because focus stays on the scanner input after garment lookup.
 */
export function isFocusOnInput(): boolean {
  const active = document.activeElement;
  if (!active) return false;

  // Allow hotkeys when focus is on scanner input — scanner safety is handled
  // by binding type constraints (modifiers for combos, duration for holds)
  if (active.hasAttribute('data-scanner-input')) return false;

  const tag = active.tagName.toLowerCase();
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
  if (active instanceof HTMLElement && active.isContentEditable) return true;

  return false;
}

/**
 * Create a string key for a combo binding to use in the comboFiredSet.
 * Format: "ctrl+alt+Enter" (modifiers sorted)
 */
function comboKey(binding: HotkeyBinding & { type: 'combo' }, key: string): string {
  return [...binding.modifiers].sort().join('+') + '+' + key;
}

/**
 * Check if a keyboard event matches a combo binding.
 * Exported for use in ShippingPanel's local handler (avoids duplicating matching logic).
 */
export function matchesCombo(e: KeyboardEvent, binding: HotkeyBinding & { type: 'combo' }): boolean {
  // Check all required modifiers are present
  for (const mod of binding.modifiers) {
    if (mod === 'ctrl' && !e.ctrlKey) return false;
    if (mod === 'alt' && !e.altKey) return false;
    if (mod === 'meta' && !e.metaKey) return false;
    if (mod === 'shift' && !e.shiftKey) return false;
  }

  // Check no extra modifiers are pressed (except shift, which is often implicit)
  if (e.ctrlKey && !binding.modifiers.includes('ctrl')) return false;
  if (e.altKey && !binding.modifiers.includes('alt')) return false;
  if (e.metaKey && !binding.modifiers.includes('meta')) return false;

  // Check key match (case-insensitive for letter keys)
  return e.key.toLowerCase() === binding.key.toLowerCase();
}

export function useHotkeyEngine(
  bindings: HotkeyMap | undefined,
  handlers: Partial<Record<HotkeyAction, () => void>>,
  opts?: UseHotkeyEngineOpts,
): UseHotkeyEngineResult {
  const paused = opts?.paused ?? false;

  // Resolve bindings: use defaults if undefined (during loading)
  const resolvedBindings = bindings ?? DEFAULT_HOTKEY_MAP;

  // Refs for hold detection state (must persist across renders without causing re-renders)
  const holdStartRef = useRef<Map<string, number>>(new Map());
  const comboFiredSetRef = useRef<Set<string>>(new Set());
  const handlersRef = useRef(handlers);
  const bindingsRef = useRef(resolvedBindings);
  const pausedRef = useRef(paused);

  // State for UI feedback
  const [activeHold, setActiveHold] = useState<string | null>(null);

  // Keep refs in sync
  handlersRef.current = handlers;
  bindingsRef.current = resolvedBindings;
  pausedRef.current = paused;

  // Flush hold state when paused transitions to true
  useEffect(() => {
    if (paused) {
      holdStartRef.current.clear();
      comboFiredSetRef.current.clear();
      setActiveHold(null);
    }
  }, [paused]);

  // Main event listeners
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Always skip if focus is on an input
      if (isFocusOnInput()) return;

      // Skip all hotkey processing when paused (modals/dialogs open)
      if (pausedRef.current) return;

      const currentBindings = bindingsRef.current;
      const currentHandlers = handlersRef.current;

      // Check all bindings for a combo match
      for (const [action, binding] of Object.entries(currentBindings)) {
        if (!binding || binding.type !== 'combo') continue;

        const handler = currentHandlers[action as HotkeyAction];
        if (!handler) continue; // Action disabled for current context

        if (matchesCombo(e, binding)) {
          e.preventDefault();
          // Track that a combo fired for this key (prevents hold double-fire)
          comboFiredSetRef.current.add(comboKey(binding, e.key));
          handler();
          return; // Only fire one action per keydown
        }
      }

      // Track hold start (only for non-repeat, non-modifier keys)
      if (!e.repeat && !['Control', 'Alt', 'Meta', 'Shift'].includes(e.key)) {
        const holdKey = e.key;
        if (!holdStartRef.current.has(holdKey)) {
          // Check if any binding uses this key as a hold
          for (const [action, binding] of Object.entries(currentBindings)) {
            if (!binding || binding.type !== 'hold') continue;
            if (!currentHandlers[action as HotkeyAction]) continue;
            if (binding.key.toLowerCase() === holdKey.toLowerCase()) {
              holdStartRef.current.set(holdKey, Date.now());
              setActiveHold(holdKey);
              // Prevent the character from being typed — but NOT when focus is
              // on the scanner input, because scanner barcodes may contain the
              // hold-bound character and it must flow through to the scanner field.
              // Scanner vs human hold is distinguished by timing (scanner <100ms,
              // hold threshold >=300ms), not by preventDefault.
              const activeEl = document.activeElement;
              if (!activeEl?.hasAttribute('data-scanner-input')) {
                e.preventDefault();
              }
              break;
            }
          }
        }
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      const holdKey = e.key;

      // Always clean up comboFiredSet for this key (even if no hold was started).
      // Must run before the startTime early-return to prevent set leaks.
      let comboFiredForKey = false;
      for (const comboStr of comboFiredSetRef.current) {
        if (comboStr.endsWith('+' + holdKey) || comboStr.endsWith('+' + holdKey.toLowerCase())) {
          comboFiredSetRef.current.delete(comboStr);
          comboFiredForKey = true;
        }
      }

      // Always clean up hold state (even when paused — prevents dangling state)
      const startTime = holdStartRef.current.get(holdKey);
      if (startTime === undefined) return;

      holdStartRef.current.delete(holdKey);
      if (holdStartRef.current.size === 0) {
        setActiveHold(null);
      }

      // Don't fire hold actions when paused or combo already fired.
      // NOTE: We intentionally do NOT check isFocusOnInput() here.
      // If a hold started on keydown (when focus was correct), useScannerAutoFocus
      // may have redirected focus to the scanner input mid-hold. The keydown focus
      // check already gated whether tracking started — trust it.
      if (pausedRef.current) return;
      if (comboFiredForKey) return;

      // Check if any hold binding matches
      const elapsed = Date.now() - startTime;
      const currentBindings = bindingsRef.current;
      const currentHandlers = handlersRef.current;

      for (const [action, binding] of Object.entries(currentBindings)) {
        if (!binding || binding.type !== 'hold') continue;
        if (binding.key.toLowerCase() !== holdKey.toLowerCase()) continue;

        const handler = currentHandlers[action as HotkeyAction];
        if (!handler) continue;

        if (elapsed >= binding.durationMs) {
          e.preventDefault();
          handler();
          return; // Only fire one action per keyup
        }
      }
    };

    const handleBlur = () => {
      // Clear all hold state when window loses focus
      holdStartRef.current.clear();
      comboFiredSetRef.current.clear();
      setActiveHold(null);
    };

    // Register on document with capture phase (fires before bubble-phase handlers)
    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('keyup', handleKeyUp, true);
    window.addEventListener('blur', handleBlur);

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('keyup', handleKeyUp, true);
      window.removeEventListener('blur', handleBlur);
    };
  }, []); // Empty deps — handlers/bindings accessed via refs

  return { activeHold };
}
